import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMessagingStore, useMessages, useCurrentConversation, useLearningHighlights, useChatStats } from '../../stores/messagingStore';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import LearningMomentCard from './LearningMomentCard';
import ChatStatsBar from './ChatStatsBar';
import { useTranslation } from 'react-i18next';
import VoiceChatInput from '../../features/voice-chat/components/VoiceChatInput';
import CaptionToggle from '../../features/voice-chat/components/CaptionToggle';
import ChatModeToggle, { type ChatMode } from '../../features/voice-chat/components/ChatModeToggle';
import { useCaptionMode } from '../../features/voice-chat/hooks/useCaptionMode';
import { VoicePlaybackQueueProvider } from '../../features/voice-chat/hooks/useVoicePlaybackQueue';

interface ChatRoomProps {
    conversationId: string;
    currentUserId: string;
}

const ChatRoom: React.FC<ChatRoomProps> = ({ conversationId, currentUserId }) => {
    const { t } = useTranslation('messaging');
    const navigate = useNavigate();
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    const messages = useMessages();
    const currentConversation = useCurrentConversation();
    const learningHighlights = useLearningHighlights();
    const chatStats = useChatStats();
    const {
        loading,
        hasMoreMessages,
        fetchMessages,
        sendMessage,
        markAsRead,
        joinConversation,
        leaveConversation,
        typingUsers,
        clearHighlights,
    } = useMessagingStore();

    // Get typing users for this conversation (must be before useEffect that uses it)
    const conversationTypingUsers = typingUsers.get(conversationId) || [];
    const isOtherUserTyping = conversationTypingUsers.length > 0;

    // Join conversation room and fetch messages
    useEffect(() => {
        joinConversation(conversationId);
        fetchMessages(conversationId, 1);
        markAsRead(conversationId);

        return () => {
            leaveConversation(conversationId);
        };
    }, [conversationId, joinConversation, leaveConversation, fetchMessages, markAsRead]);

    // Auto-scroll to bottom when new messages arrive or typing indicator shows
    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTo({
                top: messagesContainerRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [messages, isOtherUserTyping]);

    // Mark as read when viewing
    useEffect(() => {
        if (messages.length > 0) {
            markAsRead(conversationId);
        }
    }, [messages, conversationId, markAsRead]);

    const handleLoadMore = () => {
        if (!loading && hasMoreMessages) {
            const { messagePage } = useMessagingStore.getState();
            fetchMessages(conversationId, messagePage + 1);
        }
    };

    const handleSendMessage = async (content: string) => {
        await sendMessage(conversationId, content);
    };

    const otherParticipant = currentConversation?.otherParticipant;
    const displayName = otherParticipant?.fullName || otherParticipant?.username || 'Chat';

    const isBotConversation = !!otherParticipant?.isBot;
    const [chatMode, setChatMode] = useState<ChatMode>('text');
    const captionState = useCaptionMode();

    // Snapshot of message ids that were already present when this
    // conversation opened. Anything NOT in the snapshot is "newly arrived"
    // in this session and may be eligible for auto-play.
    const initialMessageIdsRef = useRef<Set<string> | null>(null);
    useEffect(() => {
        initialMessageIdsRef.current = null;
    }, [conversationId]);
    useEffect(() => {
        if (initialMessageIdsRef.current === null && messages.length > 0 && !loading) {
            initialMessageIdsRef.current = new Set(messages.map((m) => m.id));
        }
    }, [messages, loading]);

    // ONLY the most recently arrived bot voice message auto-plays. If the
    // user fires several voice turns in a row and a newer reply arrives
    // before the older one was played, the older one is skipped — playing
    // a stale message minutes after the user moved on feels broken. This
    // also gates autoplay correctly when burst is enabled in text mode
    // (each burst becomes "latest" in turn, but only the final one stays).
    const latestAutoplayId = useMemo(() => {
        if (initialMessageIdsRef.current === null) return null;
        for (let i = messages.length - 1; i >= 0; i--) {
            const m = messages[i];
            if (m.senderId === currentUserId) continue;
            if (initialMessageIdsRef.current.has(m.id)) continue;
            const meta = (m.metadata ?? {}) as Record<string, unknown>;
            const isVoice = meta.isVoiceMode === true || !!m.audioUrl;
            if (isVoice) return m.id;
        }
        return null;
    }, [messages, currentUserId]);

    const shouldAutoplayMessage = (messageId: string): boolean => {
        return messageId === latestAutoplayId;
    };

    return (
        <VoicePlaybackQueueProvider>
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white">
            {/* Header - clickable to go to profile */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
                <div
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => otherParticipant?.id && navigate(`/profile/${otherParticipant.id}`)}
                    title={t('viewProfile', 'View profile')}
                >
                    {otherParticipant?.avatarUrl ? (
                        <img
                            src={otherParticipant.avatarUrl}
                            alt={displayName}
                            className="w-10 h-10 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 font-semibold">
                            {displayName.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div>
                        <h3 className="font-semibold text-gray-900">{displayName}</h3>
                        {isOtherUserTyping && (
                            <p className="text-xs text-gray-500">{t('typing')}</p>
                        )}
                    </div>
                </div>

                {isBotConversation && (
                    <div className="ml-auto flex items-center gap-2" data-testid="voice-header-controls">
                        {chatMode === 'voice' && (
                            <CaptionToggle mode={captionState.mode} onCycle={captionState.cycle} />
                        )}
                        <ChatModeToggle mode={chatMode} onChange={setChatMode} />
                    </div>
                )}
            </div>

            {/* Messages */}
            <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-2 relative"
            >
                {/* Load More */}
                {hasMoreMessages && (
                    <div className="text-center py-2">
                        <button
                            onClick={handleLoadMore}
                            disabled={loading}
                            className="text-sm text-primary-500 hover:text-primary-600 font-medium disabled:opacity-50"
                        >
                            {loading ? t('loading') : t('loadMore')}
                        </button>
                    </div>
                )}

                {/* Message List */}
                {messages.map((message, index) => {
                    const isOwn = message.senderId === currentUserId;
                    const showAvatar =
                        !isOwn &&
                        (index === 0 || messages[index - 1].senderId !== message.senderId);

                    return (
                        <MessageBubble
                            key={message.id}
                            message={message}
                            isOwn={isOwn}
                            showAvatar={showAvatar}
                            otherParticipant={otherParticipant}
                            captionMode={captionState.mode}
                            autoPlayLatest={shouldAutoplayMessage(message.id)}
                        />
                    );
                })}

                {/* Typing Indicator */}
                {isOtherUserTyping && (
                    <div className="flex justify-start mb-1">
                        <div className="flex items-start gap-2 max-w-[75%]">
                            {/* Avatar placeholder to align with messages */}
                            <div className="flex-shrink-0 w-8 h-8">
                                {otherParticipant?.avatarUrl ? (
                                    <img
                                        src={otherParticipant.avatarUrl}
                                        alt={displayName}
                                        className="w-8 h-8 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 text-sm font-semibold">
                                        {displayName.charAt(0).toUpperCase()}
                                    </div>
                                )}
                            </div>
                            {/* Typing dots */}
                            <div className="px-4 py-3 bg-gray-100 rounded-2xl rounded-bl-md">
                                <div className="flex gap-1">
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />

                {/* Learning Moment floating cards */}
                <LearningMomentCard highlights={learningHighlights} onDismiss={clearHighlights} />
            </div>

            {/* Stats bar */}
            <ChatStatsBar stats={chatStats} />

            {/* Message Input — voice or text */}
            {isBotConversation && chatMode === 'voice' ? (
                <VoiceChatInput conversationId={conversationId} currentUserId={currentUserId} />
            ) : (
                <MessageInput
                    conversationId={conversationId}
                    onSend={handleSendMessage}
                />
            )}
        </div>
        </VoicePlaybackQueueProvider>
    );
};

export default ChatRoom;
